import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { CheckCircle2, XCircle, Clock, ArrowLeft, ArrowRight, Home, ShieldCheck, RotateCcw, Pencil, Ban, Printer, BedDouble, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { DocumentAttachments } from "@/components/DocumentAttachments";
import { todayLocalIso } from "@/lib/dateUtils";

const STATUS_COLORS = {
  pending_department: "bg-amber-100 text-amber-800 border-amber-200",
  pending_cmo: "bg-blue-100 text-blue-800 border-blue-200",
  pending_housing: "bg-purple-100 text-purple-800 border-purple-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
  cancelled: "bg-gray-100 text-gray-600 border-gray-200",
};

const STATUS_LABELS = {
  pending_department: "Pending Department Review",
  pending_cmo: "Pending CMO Review",
  pending_housing: "Pending Housing Assignment",
  approved: "Approved",
  rejected: "Rejected",
  cancelled: "Cancelled",
};

export default function RequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasRole } = useAuth();
  // Phase 56 — when opened from /calendar (or anywhere else passing state.from)
  // the back button returns to that page instead of always /dashboard.
  const returnTo = location.state?.from || "/dashboard";
  const printRef = useRef(null);
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [notes, setNotes] = useState("");
  const [housingDetails, setHousingDetails] = useState("");
  const [reassignDept, setReassignDept] = useState("");
  const [adjustDates, setAdjustDates] = useState(false);
  const [newArrival, setNewArrival] = useState("");
  const [newDeparture, setNewDeparture] = useState("");
  const [showReassign, setShowReassign] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [linkedBooking, setLinkedBooking] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reqRes, deptRes] = await Promise.all([
          api.get(`/requests/${id}`),
          api.get("/departments"),
        ]);
        setRequest(reqRes.data);
        setDepartments(deptRes.data);
        setNewArrival(reqRes.data.arrival_date || "");
        setNewDeparture(reqRes.data.departure_date || "");
      } catch {
        toast.error("Failed to load request");
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  // Look up any auto-drafted booking linked to this request so we can surface
  // a "Finalise booking" shortcut for hospitality/booking coordinators.
  useEffect(() => {
    if (!request) return;
    const eligibleRoles = ["admin", "global_admin", "hospitality_coordinator", "booking_coordinator", "cmo_delegate"];
    if (!eligibleRoles.includes(user.role)) return;
    if (!["pending_housing", "approved"].includes(request.status)) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/bookings?request_id=${encodeURIComponent(request.id)}`);
        if (!cancelled) {
          // Prefer a live draft; fall back to any non-cancelled booking for reference.
          const draft = data.find((b) => b.status === "draft");
          setLinkedBooking(draft || data.find((b) => b.status !== "cancelled") || null);
        }
      } catch {
        if (!cancelled) setLinkedBooking(null);
      }
    })();
    return () => { cancelled = true; };
  }, [request, user.role]);

  const handleAction = async (endpoint, payload) => {
    setActionLoading(true);
    try {
      const { data } = await api.put(`/requests/${id}/${endpoint}`, payload);
      setRequest(data);
      setNotes("");
      toast.success("Action completed successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeptApproval = (action) => handleAction("department-approval", { action, notes });

  const handleCmoApproval = (action) => {
    const payload = { action, notes };
    if (action === "reassign") payload.reassign_department = reassignDept;
    handleAction("cmo-approval", payload).then(() => setShowReassign(false));
  };

  const handleHousingApproval = () => {
    if (!housingDetails.trim()) {
      toast.error("Please provide housing details");
      return;
    }
    const payload = { housing_details: housingDetails, notes };
    if (adjustDates) {
      if (!newArrival || !newDeparture) {
        toast.error("Please provide both arrival and departure dates");
        return;
      }
      if (newDeparture < newArrival) {
        toast.error("Departure date must be on or after arrival date");
        return;
      }
      payload.arrival_date = newArrival;
      payload.departure_date = newDeparture;
    }
    handleAction("housing-approval", payload);
  };

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      const { data } = await api.put(`/requests/${id}/cancel`);
      setRequest(data);
      setShowCancel(false);
      toast.success("Request cancelled");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html><head><title>Visitor Summary - ${request.visitor_name}</title>
      <style>
        body { font-family: 'Segoe UI', sans-serif; padding: 40px; color: #1C1F1E; }
        h1 { font-size: 24px; margin-bottom: 4px; color: #1B4332; }
        h2 { font-size: 18px; color: #525C58; margin-top: 24px; border-bottom: 2px solid #E8E8E6; padding-bottom: 6px; }
        .header { border-bottom: 3px solid #1B4332; padding-bottom: 12px; margin-bottom: 24px; }
        .subtitle { color: #525C58; font-size: 14px; }
        .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 12px; }
        .field label { font-size: 12px; color: #7A8A84; text-transform: uppercase; letter-spacing: 0.5px; }
        .field p { font-size: 14px; margin: 2px 0 0; font-weight: 500; }
        .status { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
        .approved { background: #D1FAE5; color: #065F46; }
        .timeline { margin-top: 12px; }
        .step { padding: 8px 0; border-bottom: 1px solid #F4F4F1; }
        .step-label { font-weight: 600; font-size: 13px; }
        .step-detail { font-size: 12px; color: #525C58; }
        .footer { margin-top: 40px; padding-top: 12px; border-top: 1px solid #E8E8E6; font-size: 11px; color: #7A8A84; }
      </style></head><body>
      <div class="header">
        <h1>AIC Kapsowar Mission Hospital</h1>
        <p class="subtitle">Visitor Arrival Summary Card</p>
      </div>
      <div style="display:flex;justify-content:space-between;align-items:center;">
        <div><h2 style="border:none;padding:0;margin:0;">${request.visitor_name}</h2><p class="subtitle">${request.specialty}</p></div>
        <span class="status approved">${request.status.toUpperCase()}</span>
      </div>
      <h2>Visit Details</h2>
      <div class="grid">
        <div class="field"><label>Department</label><p>${request.department}</p></div>
        <div class="field"><label>Email</label><p>${request.visitor_email || "N/A"}</p></div>
        <div class="field"><label>Arrival</label><p>${request.arrival_date}</p></div>
        <div class="field"><label>Departure</label><p>${request.departure_date}</p></div>
        <div class="field"><label>Party Size</label><p>${request.party_size}</p></div>
        <div class="field"><label>Submitted By</label><p>${request.created_by_name}</p></div>
      </div>
      ${request.purpose ? `<div class="field" style="margin-top:12px"><label>Purpose</label><p>${request.purpose}</p></div>` : ""}
      ${request.special_needs ? `<div class="field" style="margin-top:8px"><label>Special Needs</label><p>${request.special_needs}</p></div>` : ""}
      <h2>Housing</h2>
      <div class="field"><label>Assignment</label><p>${request.housing_approval?.housing_details || "N/A"}</p></div>
      ${request.housing_approval?.notes ? `<div class="field" style="margin-top:8px"><label>Notes</label><p>${request.housing_approval.notes}</p></div>` : ""}
      <h2>Approval Timeline</h2>
      <div class="timeline">
        <div class="step"><span class="step-label">Department Head:</span> <span class="step-detail">${request.department_approval?.status || "pending"} ${request.department_approval?.approved_by_name ? "by " + request.department_approval.approved_by_name : ""}</span></div>
        <div class="step"><span class="step-label">CMO:</span> <span class="step-detail">${request.cmo_approval?.status || "pending"} ${request.cmo_approval?.approved_by_name ? "by " + request.cmo_approval.approved_by_name : ""}</span></div>
        <div class="step"><span class="step-label">Housing:</span> <span class="step-detail">${request.housing_approval?.status || "pending"} ${request.housing_approval?.approved_by_name ? "by " + request.housing_approval.approved_by_name : ""}</span></div>
      </div>
      <div class="footer">Printed on ${new Date().toLocaleString()} | AIC Kapsowar Mission Hospital Visitor Approval System</div>
      </body></html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 250);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!request) return null;

  // Phase 72 — primary and secondary roles count equally; dept match uses the
  // effective approval list (multi-dept + nested descendants) from the backend.
  const approvalDepts = (user.approval_dept_keys && user.approval_dept_keys.length)
    ? user.approval_dept_keys : (user.department ? [user.department] : []);
  const isDeptHeadForThis = hasRole("department_head") && approvalDepts.includes(request.department);
  const isCmo = hasRole("cmo_delegate");
  const canDeptApprove = request.status === "pending_department" && (isDeptHeadForThis || isCmo);
  const canCmoApprove = isCmo && request.status === "pending_cmo";
  const canHousingApprove = hasRole("hospitality_coordinator") && request.status === "pending_housing";
  const canEdit = request.created_by === user.id && request.status === "pending_department";
  const today = todayLocalIso();
  const arrivalPassed = request.arrival_date && request.arrival_date < today;
  const isClosed = ["rejected", "cancelled"].includes(request.status);
  const canCancel = !isClosed && (
    hasRole("admin", "global_admin") ||
    (hasRole("hospitality_coordinator") && !arrivalPassed) ||
    (request.created_by === user.id && !["approved", "rejected", "cancelled"].includes(request.status))
  );
  const canPrint = request.status === "approved";
  const deptWasDeclined = request.department_approval?.status === "declined";

  return (
    <div className="max-w-4xl mx-auto space-y-6 fade-in" data-testid="request-detail" ref={printRef}>
      <div className="flex items-center justify-between no-print">
        <Button variant="ghost" onClick={() => navigate(returnTo)} className="text-[#525C58] hover:text-[#1C1F1E]" data-testid="back-to-dashboard">
          <ArrowLeft className="h-4 w-4 mr-2" /> {returnTo === "/calendar" ? "Back to Calendar" : "Back to Dashboard"}
        </Button>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button variant="outline" onClick={() => navigate(`/requests/${id}/edit`)} className="border-[#E8E8E6]" data-testid="edit-request-btn">
              <Pencil className="h-4 w-4 mr-2" /> Edit
            </Button>
          )}
          {canCancel && (
            <Button variant="outline" onClick={() => setShowCancel(true)} className="border-red-200 text-red-600 hover:bg-red-50" data-testid="cancel-request-btn">
              <Ban className="h-4 w-4 mr-2" /> Cancel Request
            </Button>
          )}
          {canPrint && (
            <Button variant="outline" onClick={handlePrint} className="border-[#E8E8E6]" data-testid="print-summary-btn">
              <Printer className="h-4 w-4 mr-2" /> Print Summary
            </Button>
          )}
        </div>
      </div>

      <Card className="border-[#E8E8E6]">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-2xl text-[#1C1F1E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {request.visitor_name}
              </CardTitle>
              <div className="flex items-center gap-2 flex-wrap mt-1">
                <p className="text-[#525C58]">{request.specialty}</p>
                {request.is_student && (
                  <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-[10px] uppercase tracking-wide" data-testid="level-badge-student">Student</Badge>
                )}
                {request.is_resident && (
                  <Badge className="bg-blue-100 text-blue-800 border border-blue-200 text-[10px] uppercase tracking-wide" data-testid="level-badge-resident">Resident</Badge>
                )}
                {!request.is_student && !request.is_resident && (
                  <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px] uppercase tracking-wide" data-testid="level-badge-faculty">Faculty</Badge>
                )}
              </div>
              {request.created_by_name && (
                <p className="text-xs text-[#7A8A84] mt-2" data-testid="request-submitted-by-header">
                  Submitted by <span className="font-medium text-[#1C1F1E]">{request.created_by_name}</span>
                  {request.created_at && (
                    <span> · {new Date(request.created_at).toLocaleDateString()}</span>
                  )}
                </p>
              )}
            </div>
            <Badge className={`${STATUS_COLORS[request.status] || "bg-gray-100 text-gray-600"} border text-sm px-3 py-1`} data-testid="request-status-badge">
              {STATUS_LABELS[request.status] || request.status}
            </Badge>
          </div>
        </CardHeader>
        <Separator />
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            <InfoField label="Department" value={request.department} />
            <InfoField label="Email" value={request.visitor_email || "N/A"} />
            <InfoField label="Party Size" value={request.party_size} />
            <InfoField
              label="Arrival Date"
              value={
                request.housing_approval?.dates_adjusted
                  ? `${request.arrival_date} (was ${request.housing_approval.original_arrival_date})`
                  : request.arrival_date
              }
            />
            <InfoField
              label="Departure Date"
              value={
                request.housing_approval?.dates_adjusted
                  ? `${request.departure_date} (was ${request.housing_approval.original_departure_date})`
                  : request.departure_date
              }
            />
            <InfoField label="Submitted By" value={request.created_by_name} />
          </div>
          {request.housing_approval?.dates_adjusted && (
            <p className="mt-3 text-xs text-amber-700" data-testid="dates-adjusted-badge">
              Dates adjusted by hospitality coordinator during housing approval.
            </p>
          )}
          {request.purpose && <div className="mt-6"><InfoField label="Purpose" value={request.purpose} /></div>}
          {request.special_needs && <div className="mt-4"><InfoField label="Special Needs" value={request.special_needs} /></div>}
          {request.housing_approval?.housing_details && (
            <div className="mt-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <InfoField label="Housing Assignment" value={request.housing_approval.housing_details} />
              {linkedBooking && linkedBooking.status !== "draft" && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate("/bookings", { state: { openBookingId: linkedBooking.id } })}
                  className="mt-2 text-emerald-800 hover:bg-emerald-100 -ml-2"
                  data-testid="open-linked-booking-btn"
                >
                  <BedDouble className="h-4 w-4 mr-2" /> Open linked booking
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Attachments (Phase 59) — any user with view access can upload; download gated by permission */}
      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>Attachments</CardTitle>
        </CardHeader>
        <CardContent>
          <DocumentAttachments entityType="request" entityId={request.id} compact />
        </CardContent>
      </Card>

      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>Approval Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row items-stretch gap-4" data-testid="approval-timeline">
            <ApprovalStep label="Department Head" status={request.department_approval?.status} by={request.department_approval?.approved_by_name} at={request.department_approval?.approved_at} notes={request.department_approval?.notes} onBehalf={request.department_approval?.on_behalf} />
            <div className="hidden md:flex items-center"><ArrowRight className="h-5 w-5 text-[#E8E8E6]" /></div>
            <ApprovalStep label="Chief Medical Officer" status={request.cmo_approval?.status} by={request.cmo_approval?.approved_by_name} at={request.cmo_approval?.approved_at} notes={request.cmo_approval?.notes} />
            <div className="hidden md:flex items-center"><ArrowRight className="h-5 w-5 text-[#E8E8E6]" /></div>
            <ApprovalStep label="Housing" status={request.housing_approval?.status} by={request.housing_approval?.approved_by_name} at={request.housing_approval?.approved_at} notes={request.housing_approval?.notes} />
          </div>
        </CardContent>
      </Card>

      {(canDeptApprove || canCmoApprove || canHousingApprove) && (
        <Card className="border-[#E8E8E6] border-l-4 border-l-[#1B4332]" data-testid="action-panel">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>Your Action Required</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#1C1F1E]">Notes (optional)</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add notes for this decision..." rows={2} data-testid="action-notes-input" />
            </div>

            {canDeptApprove && (
              <div className="space-y-3">
                {isCmo && !isDeptHeadForThis && (
                  <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm" data-testid="cmo-on-behalf-hint">
                    You are approving on behalf of the <strong>{request.department}</strong> department head. This will be recorded in the audit trail.
                  </div>
                )}
                <div className="flex gap-3">
                  <Button onClick={() => handleDeptApproval("approve")} className="bg-emerald-600 hover:bg-emerald-700" disabled={actionLoading} data-testid="dept-approve-btn">
                    <CheckCircle2 className="h-4 w-4 mr-2" /> Approve{isCmo && !isDeptHeadForThis ? " on behalf" : ""}
                  </Button>
                  <Button onClick={() => handleDeptApproval("decline")} variant="destructive" disabled={actionLoading} data-testid="dept-decline-btn">
                    <XCircle className="h-4 w-4 mr-2" /> Decline
                  </Button>
                </div>
              </div>
            )}

            {canCmoApprove && (
              <div className="flex gap-3 flex-wrap">
                <Button onClick={() => handleCmoApproval("approve")} className="bg-emerald-600 hover:bg-emerald-700" disabled={actionLoading} data-testid="cmo-approve-btn">
                  <CheckCircle2 className="h-4 w-4 mr-2" /> Approve
                </Button>
                {deptWasDeclined && (
                  <Button onClick={() => handleCmoApproval("override")} className="bg-amber-600 hover:bg-amber-700" disabled={actionLoading} data-testid="cmo-override-btn">
                    <ShieldCheck className="h-4 w-4 mr-2" /> Override Decline
                  </Button>
                )}
                <Button onClick={() => handleCmoApproval("decline")} variant="destructive" disabled={actionLoading} data-testid="cmo-decline-btn">
                  <XCircle className="h-4 w-4 mr-2" /> Decline
                </Button>
                <Button variant="outline" onClick={() => setShowReassign(true)} disabled={actionLoading} className="border-[#E8E8E6]" data-testid="cmo-reassign-btn">
                  <RotateCcw className="h-4 w-4 mr-2" /> Reassign
                </Button>
              </div>
            )}

            {canHousingApprove && (
              <div className="space-y-4">
                {linkedBooking && linkedBooking.status === "draft" && (
                  <div
                    className="rounded-lg border border-amber-300 bg-amber-50/70 p-4"
                    data-testid="finalise-booking-callout"
                  >
                    <div className="flex items-start gap-3">
                      <BedDouble className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-amber-900">
                          A draft booking is ready for this visitor.
                        </p>
                        <p className="text-xs text-amber-800/90 mt-0.5">
                          Finalise in the Bookings module to assign a unit, confirm dates and apply a rate card.
                          Saving as <span className="font-medium">Confirmed</span> will auto-approve housing and email the visitor.
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => navigate("/bookings", { state: { openBookingId: linkedBooking.id } })}
                        className="bg-[#1B4332] hover:bg-[#2D6A4F] shrink-0"
                        data-testid="finalise-booking-btn"
                      >
                        Finalise booking <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                    <p className="mt-3 text-[11px] text-amber-800/70 border-t border-amber-200 pt-2">
                      Or, use the quick approval form below if you don't need to track rooms or invoicing for this visitor.
                    </p>
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-[#1C1F1E]">Housing Details *</Label>
                  <Input value={housingDetails} onChange={(e) => setHousingDetails(e.target.value)} placeholder="e.g. Guest House Room 3, Meals included" data-testid="housing-details-input" />
                </div>

                <div className="rounded-md border border-amber-200 bg-amber-50/70 p-4 space-y-3" data-testid="adjust-dates-wrap">
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={adjustDates}
                      onChange={(e) => setAdjustDates(e.target.checked)}
                      className="mt-1 h-4 w-4 accent-[#1B4332] cursor-pointer"
                      data-testid="adjust-dates-toggle"
                    />
                    <span className="text-sm text-amber-900">
                      <span className="font-medium">Adjust visit dates</span>
                      <span className="block text-amber-800/80 text-xs mt-0.5">
                        Only tick this if housing availability requires different dates. The change is recorded in the audit trail.
                      </span>
                    </span>
                  </label>
                  {adjustDates && (
                    <div className="grid grid-cols-2 gap-3 pt-1">
                      <div className="space-y-1">
                        <Label htmlFor="new-arrival" className="text-xs text-amber-900">Arrival</Label>
                        <Input
                          id="new-arrival"
                          type="date"
                          value={newArrival}
                          onChange={(e) => setNewArrival(e.target.value)}
                          className="bg-white"
                          data-testid="housing-new-arrival"
                        />
                        <p className="text-[11px] text-amber-800/70">Originally {request.arrival_date}</p>
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="new-departure" className="text-xs text-amber-900">Departure</Label>
                        <Input
                          id="new-departure"
                          type="date"
                          value={newDeparture}
                          onChange={(e) => setNewDeparture(e.target.value)}
                          className="bg-white"
                          data-testid="housing-new-departure"
                        />
                        <p className="text-[11px] text-amber-800/70">Originally {request.departure_date}</p>
                      </div>
                    </div>
                  )}
                </div>

                <Button onClick={handleHousingApproval} className="bg-[#1B4332] hover:bg-[#2D6A4F]" disabled={actionLoading} data-testid="housing-approve-btn">
                  <Home className="h-4 w-4 mr-2" /> Approve Housing
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={showReassign} onOpenChange={setShowReassign}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reassign to Department</DialogTitle>
            <DialogDescription>Select a new department for this visitor request</DialogDescription>
          </DialogHeader>
          <Select value={reassignDept} onValueChange={setReassignDept}>
            <SelectTrigger data-testid="reassign-department-select"><SelectValue placeholder="Select department" /></SelectTrigger>
            <SelectContent>
              {departments.filter((d) => (d.key || d) !== request.department).map((d) => (
                <SelectItem key={d.id || d} value={d.key || d}>{d.name || d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReassign(false)}>Cancel</Button>
            <Button onClick={() => handleCmoApproval("reassign")} className="bg-[#1B4332] hover:bg-[#2D6A4F]" disabled={!reassignDept || actionLoading} data-testid="confirm-reassign-btn">Reassign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCancel} onOpenChange={setShowCancel}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Request</DialogTitle>
            <DialogDescription>Are you sure you want to cancel this visitor request for {request.visitor_name}? This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancel(false)}>Keep Request</Button>
            <Button variant="destructive" onClick={handleCancel} disabled={actionLoading} data-testid="confirm-cancel-btn">
              <Ban className="h-4 w-4 mr-2" /> Yes, Cancel Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <p className="text-sm text-[#7A8A84] mb-1">{label}</p>
      <p className="text-[#1C1F1E]">{value}</p>
    </div>
  );
}

function ApprovalStep({ label, status, by, at, notes, onBehalf }) {
  const getIcon = () => {
    if (status === "approved" || status === "overridden") return <CheckCircle2 className="h-6 w-6 text-emerald-600" />;
    if (status === "declined") return <XCircle className="h-6 w-6 text-red-500" />;
    if (status === "reassigned") return <RotateCcw className="h-6 w-6 text-amber-500" />;
    return <Clock className="h-6 w-6 text-[#7A8A84]" />;
  };

  const getBgColor = () => {
    if (status === "approved" || status === "overridden") return "bg-emerald-50 border-emerald-200";
    if (status === "declined") return "bg-red-50 border-red-200";
    if (status === "reassigned") return "bg-amber-50 border-amber-200";
    return "bg-[#F4F4F1] border-[#E8E8E6]";
  };

  return (
    <div className={`flex-1 p-4 border rounded-lg ${getBgColor()} transition-all duration-200`}>
      <div className="flex items-center gap-2 mb-2">
        {getIcon()}
        <span className="font-medium text-[#1C1F1E] text-sm">{label}</span>
      </div>
      <p className="text-sm text-[#525C58]">
        {status === "pending" ? "Awaiting review" : `${status}${by ? ` by ${by}` : ""}`}
      </p>
      {onBehalf && status && status !== "pending" && (
        <p className="text-xs text-amber-700 mt-1" data-testid="on-behalf-badge">(on behalf, by CMO)</p>
      )}
      {at && <p className="text-xs text-[#7A8A84] mt-1">{new Date(at).toLocaleString()}</p>}
      {notes && <p className="text-xs text-[#7A8A84] mt-1 italic">"{notes}"</p>}
    </div>
  );
}
